import React from "react";
import { Box, Container, Typography, Link } from "@mui/joy";

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography
    level="h3"
    sx={{
      fontWeight: 600,
      fontSize: "1.25rem",
      color: "#1A1B1D",
      mb: 1,
      mt: 3,
    }}
  >
    {children}
  </Typography>
);

const SectionText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    level="body-lg"
    sx={{
      lineHeight: 1.6,
      color: "#7B7F85",
      mb: 2,
    }}
  >
    {children}
  </Typography>
);

const SubsectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography
    level="h4"
    sx={{
      fontWeight: 600,
      fontSize: "1.1rem",
      color: "#1A1B1D",
      mb: 0.5,
      mt: 2,
    }}
  >
    {children}
  </Typography>
);

const BulletList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="ul"
    sx={{
      pl: 3,
      mb: 2,
      "& li": {
        color: "#7B7F85",
        lineHeight: 1.6,
        mb: 0.5,
      },
    }}
  >
    {children}
  </Box>
);

const PrivacyPolicyPage: React.FC = () => {
  return (
    <Box sx={{ minHeight: '100vh', py: 4, backgroundColor: '#F8F9FA' }}>
      <Container maxWidth="md">
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '16px',
            p: { xs: 3, md: 6 },
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <Typography
            level="h1"
            sx={{
              fontSize: { xs: "2rem", md: "2.5rem" },
              fontWeight: 600,
              color: "#1A1B1D",
              mb: 2,
              textAlign: 'center',
            }}
          >
            Privacy Policy
          </Typography>

          <Typography
            level="body-md"
            sx={{
              color: "#7B7F85",
              mb: 3,
              textAlign: 'center',
              fontStyle: 'italic',
            }}
          >
            Effective Date: August 1, 2025
          </Typography>

          <SectionText>
            This Privacy Policy explains how we collect, use, share, and protect your 
            personal information when you upload a video or submit application materials 
            through our website (the <strong>"Service"</strong>). By using the Service, 
            you agree to the terms outlined below.
          </SectionText>

          <SectionTitle>1. Information We Collect</SectionTitle>
          <SectionText>
            When you use our Service, we may collect the following information:
          </SectionText>
          <BulletList>
            <li><strong>Video Content:</strong> Any video you upload as part of your job application.</li>
            <li><strong>Application Materials:</strong> Your résumé, contact details, and other information you provide.</li>
            <li><strong>Contact Information:</strong> Your phone number, email address, and other identifiers.</li>
            <li><strong>Technical Data:</strong> Device type, browser, and usage information for security and improvement.</li>
          </BulletList>

          <SectionTitle>2. How We Use Your Information</SectionTitle>
          <SectionText>
            We use your information for the following purposes:
          </SectionText>
          <BulletList>
            <li><strong>Sharing with Employers:</strong> To share your Video and application materials with companies or employers interested in hiring you.</li>
            <li><strong>Communication:</strong> To send SMS messages for phone verification, job opportunities, and updates. Message and data rates may apply. You can opt out by replying "STOP" or clicking the unsubscribe link.</li>
            <li><strong>Evaluation:</strong> To review, rate, and assess your Video and application for recruitment purposes.</li>
            <li><strong>Research and Training:</strong> To use your Video internally for research, training, and improving candidate evaluation methods.</li>
            <li><strong>Service Improvement:</strong> To analyze and enhance the Service.</li>
          </BulletList>

          <SectionTitle>3. How We Share Information</SectionTitle>
          <SectionText>
            We may share your information with:
          </SectionText>
          <BulletList>
            <li><strong>Employers and Partners:</strong> Companies interested in reviewing your application.</li>
            <li><strong>Service Providers:</strong> Vendors who help us operate our Service (e.g., cloud storage, messaging providers).</li>
            <li><strong>Legal Obligations:</strong> When required by law, regulation, or legal process.</li>
          </BulletList>
          <SectionText>
            We do not sell your personal information.
          </SectionText>
          <SectionText>
            We will not share your opt-in to an SMS campaign with any third party for purposes unrelated to providing you with the services of that campaign. We may share your Personal Data, including your SMS opt-in or consent status, with third parties that help us provide our messaging services, including but not limited to platform providers, phone companies, and any other vendors who assist us in the delivery of text messages.
          </SectionText>
          <SectionText>
            All the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.
          </SectionText>

          <SectionTitle>4. Data Retention</SectionTitle>
          <SectionText>
            We retain your Video and application materials as long as necessary to fulfill 
            the purposes described in this policy. You may request deletion at any time, 
            subject to legal and operational requirements.
          </SectionText>

          <SectionTitle>5. Your Rights</SectionTitle>
          <SectionText>
            You have the right to:
          </SectionText>
          <BulletList>
            <li>Request access to the personal data we hold about you.</li>
            <li>Request correction of inaccurate information.</li>
            <li>Request deletion of your data, subject to legal limitations.</li>
          </BulletList>
          <SectionText>
            To exercise these rights, please contact us at{" "}
            <Link 
              href="mailto:dalton@geekapp.co" 
              sx={{ color: "#2E2D7E", fontWeight: 600 }}
            >
              dalton@geekapp.co
            </Link>.
          </SectionText>

          <SectionTitle>6. Security</SectionTitle>
          <SectionText>
            We implement reasonable safeguards to protect your personal information. 
            However, no method of transmission over the Internet or electronic storage 
            is completely secure, and we cannot guarantee absolute security.
          </SectionText>

          <SectionTitle>7. Children's Privacy</SectionTitle>
          <SectionText>
            Our Service is not intended for individuals under 16 years of age. We do 
            not knowingly collect personal information from children.
          </SectionText>

          <SectionTitle>8. Changes to This Policy</SectionTitle>
          <SectionText>
            We may update this Privacy Policy from time to time. Changes will be posted 
            on this page with the updated effective date.
          </SectionText>

          <SectionTitle>9. Contact Us</SectionTitle>
          <SectionText>
            If you have questions about this Privacy Policy or how your information is 
            handled, please contact us at:
          </SectionText>
          <SectionText>
            <strong>Email:</strong>{" "}
            <Link 
              href="mailto:dalton@geekapp.co" 
              sx={{ color: "#2E2D7E", fontWeight: 600 }}
            >
              dalton@geekapp.co
            </Link>
          </SectionText>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
